import { useState, useEffect } from 'react';
import {
  getFeedback,
  sendCoachFeedback,
  updateCoachFeedback,
} from '../../../../api/reports.api';

export default function FeedbackForm({ reportId, onSubmitSuccess }) {
  const [loading, setLoading] = useState(true);
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // ---------------- STATE ---------------- //
  const [form, setForm] = useState(defaultForm());

  function defaultForm() {
    return {
      comment: '',
      score: '',
      nextPeriodPlan: {
        dailyCalories: '',
        proteinPercent: '',
        carbsPercent: '',
        fatPercent: '',
        fiberTarget: '',
        dailyStepsTarget: '',
        trainingDaysTarget: '',
        cardioDaysTarget: '',
      },
    };
  }

  // ---------------- LOAD FEEDBACK ---------------- //
  useEffect(() => {
    loadFeedback();
  }, [reportId]);

  async function loadFeedback() {
    setLoading(true);
    try {
      const res = await getFeedback(reportId);
      const fb = res.data?.feedback ?? null;

      // بررسی اینکه فیدبک واقعی وجود دارد
      const hasRealFeedback =
        fb &&
        ((fb.comment && fb.comment.trim() !== '') ||
          (typeof fb.score === 'number' && fb.score !== null) ||
          (fb.nextPeriodPlan &&
            Object.values(fb.nextPeriodPlan).some(
              (v) => v !== null && v !== '' && v !== undefined
            )));

      if (hasRealFeedback) {
        setExistingFeedback(fb);
        setForm({
          comment: fb.comment || '',
          score: fb.score || '',
          nextPeriodPlan: {
            dailyCalories: fb.nextPeriodPlan?.dailyCalories ?? '',
            proteinPercent: fb.nextPeriodPlan?.proteinPercent ?? '',
            carbsPercent: fb.nextPeriodPlan?.carbsPercent ?? '',
            fatPercent: fb.nextPeriodPlan?.fatPercent ?? '',
            fiberTarget: fb.nextPeriodPlan?.fiberTarget ?? '',
            dailyStepsTarget: fb.nextPeriodPlan?.dailyStepsTarget ?? '',
            trainingDaysTarget: fb.nextPeriodPlan?.trainingDaysTarget ?? '',
            cardioDaysTarget: fb.nextPeriodPlan?.cardioDaysTarget ?? '',
          },
        });

        setIsEditing(false); // حالت نمایش
      } else {
        // هیچ فیدبک معناداری وجود ندارد → حالت ساخت
        setExistingFeedback(null);
        setForm({
          comment: '',
          score: '',
          nextPeriodPlan: {
            dailyCalories: '',
            proteinPercent: '',
            carbsPercent: '',
            fatPercent: '',
            fiberTarget: '',
            dailyStepsTarget: '',
            trainingDaysTarget: '',
            cardioDaysTarget: '',
          },
        });
        setIsEditing(true);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  // ---------------- HANDLE INPUT ---------------- //
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('nextPeriodPlan.')) {
      const key = name.split('.')[1];
      setForm((p) => ({
        ...p,
        nextPeriodPlan: { ...p.nextPeriodPlan, [key]: value },
      }));
      return;
    }

    setForm((p) => ({ ...p, [name]: value }));
  };

  // ---------------- VALIDATION ---------------- //
  function validateForm() {
    const f = form;

    if (!f.comment.trim()) {
      alert('نوشتن نظر مربی الزامی است');
      return false;
    }

    if (!f.score || Number(f.score) < 1 || Number(f.score) > 10) {
      alert('امتیاز باید بین ۱ تا ۱۰ باشد');
      return false;
    }

    const np = f.nextPeriodPlan;

    if (!np.dailyCalories || Number(np.dailyCalories) <= 0) {
      alert('کالری روزانه باید وارد شود');
      return false;
    }

    const p = Number(np.proteinPercent);
    const c = Number(np.carbsPercent);
    const fat = Number(np.fatPercent);

    if ([p, c, fat].some((x) => !x && x !== 0)) {
      alert('درصدهای ماکرو باید وارد شوند');
      return false;
    }

    if (p + c + fat !== 100) {
      alert('مجموع درصدهای ماکرو باید ۱۰۰٪ باشد');
      return false;
    }

    if (!np.fiberTarget) {
      alert('هدف فیبر وارد نشده است');
      return false;
    }

    if (!np.dailyStepsTarget || Number(np.dailyStepsTarget) <= 0) {
      alert('هدف قدم باید وارد شود');
      return false;
    }

    if (np.trainingDaysTarget == null || Number(np.trainingDaysTarget) < 0) {
      alert('روز تمرین معتبر نیست');
      return false;
    }

    return true;
  }

  // ---------------- CALCULATE MACRO GRAMS ---------------- //
  function macroGrams() {
    const cal = Number(form.nextPeriodPlan.dailyCalories);
    const p = Number(form.nextPeriodPlan.proteinPercent);
    const c = Number(form.nextPeriodPlan.carbsPercent);
    const f = Number(form.nextPeriodPlan.fatPercent);

    if (!cal || !p || !c || !f) return { pg: 0, cg: 0, fg: 0 };

    return {
      pg: Math.round((cal * p) / 100 / 4),
      cg: Math.round((cal * c) / 100 / 4),
      fg: Math.round((cal * f) / 100 / 9),
    };
  }

  const { pg, cg, fg } = macroGrams();

  // ---------------- SUBMIT ---------------- //
  const submit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const payload = {
      comment: form.comment,
      score: Number(form.score),
      nextPeriodPlan: {
        dailyCalories: Number(form.nextPeriodPlan.dailyCalories),
        proteinPercent: Number(form.nextPeriodPlan.proteinPercent),
        carbsPercent: Number(form.nextPeriodPlan.carbsPercent),
        fatPercent: Number(form.nextPeriodPlan.fatPercent),
        fiberTarget: Number(form.nextPeriodPlan.fiberTarget),
        dailyStepsTarget: Number(form.nextPeriodPlan.dailyStepsTarget),
        trainingDaysTarget: Number(form.nextPeriodPlan.trainingDaysTarget),
        cardioDaysTarget: Number(form.nextPeriodPlan.cardioDaysTarget),
      },
    };

    try {
      if (existingFeedback) {
        await updateCoachFeedback(reportId, payload);
        alert('فیدبک ویرایش شد');
      } else {
        console.log('payload', payload);

        await sendCoachFeedback(reportId, payload);
        alert('فیدبک ثبت شد');
      }

      onSubmitSuccess?.();
      loadFeedback();
    } catch (err) {
      console.error(err);
      alert('ثبت فیدبک با خطا مواجه شد');
    }
  };

  // ---------------- RENDER ---------------- //

  if (loading) return <p>در حال بارگذاری...</p>;

  // VIEW MODE
  if (existingFeedback && !isEditing) {
    return (
      <div className="bg-white p-4 rounded-xl border shadow-sm">
        <h3 className="font-bold mb-3">فیدبک مربی</h3>

        <p className="mb-2">{form.comment}</p>
        <p className="text-sm text-gray-600 mb-1">امتیاز: {form.score}</p>

        <button
          className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-xl"
          onClick={() => setIsEditing(true)}>
          ویرایش
        </button>
      </div>
    );
  }

  // EDIT / CREATE MODE
  return (
    <form
      onSubmit={submit}
      className="bg-white p-5 rounded-xl border shadow-sm">
      <h3 className="font-bold mb-3">
        {existingFeedback ? 'ویرایش فیدبک' : 'ثبت فیدبک جدید'}
      </h3>

      <textarea
        name="comment"
        className="w-full border p-3 rounded-xl mb-3"
        placeholder="نظر مربی..."
        value={form.comment}
        onChange={handleChange}
      />

      <input
        name="score"
        type="number"
        className="w-32 border p-2 rounded mb-4"
        placeholder="امتیاز ۱ تا ۱۰"
        value={form.score}
        onChange={handleChange}
      />

      <h4 className="font-bold mt-4 mb-2">برنامه دوره بعد</h4>

      <div className="grid grid-cols-3 gap-4">
        <MacroInput
          label="کالری روزانه"
          name="nextPeriodPlan.dailyCalories"
          value={form.nextPeriodPlan.dailyCalories}
          onChange={handleChange}
        />

        <MacroInput
          label={`پروتئین (%) — ${pg} گرم`}
          name="nextPeriodPlan.proteinPercent"
          value={form.nextPeriodPlan.proteinPercent}
          onChange={handleChange}
        />

        <MacroInput
          label={`کربوهیدرات (%) — ${cg} گرم`}
          name="nextPeriodPlan.carbsPercent"
          value={form.nextPeriodPlan.carbsPercent}
          onChange={handleChange}
        />

        <MacroInput
          label={`چربی (%) — ${fg} گرم`}
          name="nextPeriodPlan.fatPercent"
          value={form.nextPeriodPlan.fatPercent}
          onChange={handleChange}
        />

        <MacroInput
          label="فیبر (گرم)"
          name="nextPeriodPlan.fiberTarget"
          value={form.nextPeriodPlan.fiberTarget}
          onChange={handleChange}
        />

        <MacroInput
          label="هدف قدم"
          name="nextPeriodPlan.dailyStepsTarget"
          value={form.nextPeriodPlan.dailyStepsTarget}
          onChange={handleChange}
        />

        <MacroInput
          label="روز تمرین"
          name="nextPeriodPlan.trainingDaysTarget"
          value={form.nextPeriodPlan.trainingDaysTarget}
          onChange={handleChange}
        />
        <MacroInput
          label="هوازی در هفته/دقیقه "
          name="nextPeriodPlan.cardioDaysTarget"
          value={form.nextPeriodPlan.cardioDaysTarget}
          onChange={handleChange}
        />
      </div>

      <div className="flex justify-end gap-2 mt-4">
        {existingFeedback && (
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="px-4 py-2 bg-gray-100 rounded-xl">
            لغو
          </button>
        )}

        <button
          type="submit"
          className="px-5 py-2 bg-blue-600 text-white rounded-xl">
          ذخیره
        </button>
      </div>
    </form>
  );
}

// ---------------- SMALL COMPONENT ---------------- //
function MacroInput({ label, value, name, onChange }) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-1">{label}</label>
      <input
        type="number"
        name={name}
        value={value}
        onChange={onChange}
        className="w-full border p-2 rounded"
      />
    </div>
  );
}
